import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import glassTheme from '../../styles/glassTheme';
import { DashboardLayout } from '../../components/DashboardLayout';
import { getAllTopics, updateTopic, Topic, createMainCategory, createSubcategory, createTopic, getTopicBySlug, getTopicById, saveExerciseVersion } from '../../services/topicsApi';
import { pickLatestExercise, readExerciseHtml } from '../../utils/exerciseVersions';
import { TOPICS_HIERARCHY, FLAT_TOPICS } from '../../data/excelTopics';
import toast from 'react-hot-toast';
import axios from 'axios';
import Editor, { OnMount, BeforeMount } from '@monaco-editor/react';
import type Monaco from 'monaco-editor';

// Dummy worker class to prevent Monaco worker errors
class DummyWorker {
  onmessage: ((message: any) => void) | null = null;
  onerror: ((error: any) => void) | null = null;

  postMessage(message: any): void {
    // Silently ignore messages - no actual processing needed
  }

  terminate(): void {
    // No-op
  }

  addEventListener(type: string, listener: any): void {
    if (type === 'message') {
      this.onmessage = listener;
    }
  }

  removeEventListener(type: string, listener: any): void {
    if (type === 'message') {
      this.onmessage = null;
    }
  }
}

// Helper function to create a unique, validated key for topic map
// Uses composite key to prevent duplicates and matches lookup strategy
const createTopicMapKey = (topic: Topic): string | null => {
  // Prioritize slug, then id, then title (consistent with lookup strategy)
  const slug = topic.slug?.toLowerCase()?.trim();
  const id = topic.id?.toLowerCase()?.trim();
  const title = topic.title?.toLowerCase()?.trim();

  const key = slug || id || title;

  // Validate key: must be non-empty string after trimming
  if (!key || key.length === 0) {
    return null;
  }

  return key;
};

// Helper function to build topic map from API response
// Ensures consistent key generation and proper validation
const buildTopicMap = (topics: Topic[]): Map<string, Topic> => {
  const topicMap = new Map<string, Topic>();

  topics.forEach((apiTopic: Topic) => {
    const key = createTopicMapKey(apiTopic);
    if (key) {
      // Check for duplicates to prevent silent data loss
      if (topicMap.has(key)) {
        console.warn(`Duplicate topic key detected: ${key}. Previous topic will be overwritten.`, {
          existing: topicMap.get(key),
          new: apiTopic
        });
      }
      topicMap.set(key, apiTopic);
    }
  });

  return topicMap;
};

// Helper function to get token from authStore
const getToken = () => {
  return useAuthStore.getState().token;
};

const Header = styled.div`
  margin-bottom: 2rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  align-items: start;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SidebarContainer = styled.div`
  ${glassTheme.glass.container}
  border-radius: 12px;
  border: 1px solid ${glassTheme.colors.border};
  display: flex;
  flex-direction: column;
`;

const SidebarTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
  padding: 1rem;
  text-align: center;
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const TopicList = styled.div`
  overflow-y: auto;
  max-height: 600px;
  padding: 0.75rem 0;
`;

const DraftFilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 184, 0, 0.12);
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
  font-size: 0.85rem;

  button {
    background: none;
    border: 1px solid ${glassTheme.colors.border};
    border-radius: 6px;
    padding: 0.15rem 0.6rem;
    color: ${glassTheme.colors.text.primary};
    cursor: pointer;
    font-size: 0.8rem;

    &:hover {
      border-color: ${glassTheme.colors.borderHover};
    }
  }
`;

const CollapseIcon = styled.span<{ $collapsed: boolean }>`
  display: inline-block;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$collapsed ? '-90deg' : '0deg'});
  font-size: 0.7rem;
  margin-right: 0.35rem;
`;

const TopicCategoryLabel = styled.div<{ $color: string; $isSubCategory?: boolean }>`
  padding: ${props => props.$isSubCategory ? '0.35rem 1.25rem 0.25rem 1.75rem' : '0.5rem 1.25rem 0.35rem'};
  font-size: ${props => props.$isSubCategory ? '0.9rem' : '1rem'};
  font-weight: 600;
  text-transform: ${props => props.$isSubCategory ? 'none' : 'uppercase'};
  letter-spacing: ${props => props.$isSubCategory ? 'normal' : '0.05em'};
  color: ${props => props.$color};
  border-left: 3px solid ${props => props.$color};
  margin-top: ${props => props.$isSubCategory ? '0.25rem' : '0.5rem'};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const TopicListItem = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 0.65rem 1.25rem 0.65rem 2.5rem;
  background: ${props => props.$active
    ? 'rgba(96, 165, 250, 0.15)'
    : 'transparent'};
  border: none;
  border-left: 3px solid ${props => props.$active
    ? glassTheme.colors.accent.blue
    : 'transparent'};
  color: ${glassTheme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  opacity: ${props => props.$active ? '1' : '0.8'};

  &:hover {
    background: rgba(96, 165, 250, 0.1);
    opacity: 1;
  }
`;

const TopicListItemText = styled.span`
  flex: 1;
  font-size: 0.95rem;
  color: ${glassTheme.colors.text.primary};
`;

const HeartCheckbox = styled.span`
  font-size: 1rem;
  margin-right: 0.5rem;
  cursor: default;
  filter: drop-shadow(0 0 4px rgba(255, 105, 180, 0.3));
`;

const EditorPanel = styled.div`
  ${glassTheme.glass.container}
  border-radius: 12px;
  border: 1px solid ${glassTheme.colors.border};
  min-height: 600px;
  display: flex;
  flex-direction: column;
`;

const EditorHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EditorTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.primary};
`;

const SaveButton = styled.button`
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 222, 128, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EditorModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid ${glassTheme.colors.border};
  flex-shrink: 0;
`;

const EditorModeLabel = styled.span`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.875rem;
  font-weight: 500;
`;

const EditorModeButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 0.75rem;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  color: ${props => props.$active ? '#8b5cf6' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background: ${props => props.$active ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const EditorContent = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  border-radius: 0;
  border-bottom: 2px solid ${props => props.$active ? glassTheme.colors.accent.blue : 'transparent'};
  color: ${props => props.$active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};
  cursor: pointer;
  transition: all ${glassTheme.transition.fast};
  font-size: 0.875rem;

  &:hover {
    color: ${glassTheme.colors.text.primary};
    border-bottom-color: ${glassTheme.colors.accent.blue};
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.purple};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.purple};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: ${glassTheme.colors.text.muted};
  text-align: center;

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  ${glassTheme.glass.container}
  border-radius: 12px;
  border: 1px solid ${glassTheme.colors.border};
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 1.3rem;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 1.5rem 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)';
      case 'danger': return 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => props.$variant ? 'white' : glassTheme.colors.text.primary};
  border: ${props => props.$variant ? 'none' : '1px solid ' + glassTheme.colors.border};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const IconButton = styled.button`
  padding: 0.4rem 0.6rem;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.5);
  border-radius: 6px;
  color: #8b5cf6;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  margin-left: 0.5rem;

  &:hover {
    background: rgba(139, 92, 246, 0.3);
  }
`;

// New styled components for content generation
const ContentGenerationPanel = styled.div`
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const FileInputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
`;

const FileInput = styled.input`
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 6px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.purple};
  }
`;

const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.purple};
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const GenerateButton = styled.button<{ $loading?: boolean }>`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(96, 165, 250, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ViewModeToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CodeEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  overflow: hidden;
`;

const CodeEditorWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  border-radius: 8px;
  border: 1px solid ${glassTheme.colors.border};
  overflow: hidden;
  background: #1e1e1e;
`;

const IframeContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid ${glassTheme.colors.border};
`;

const IframeWrapper = styled.iframe`
  flex: 1;
  width: 100%;
  min-height: 400px;
  border: none;
  background: white;
`;

const RenderedContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  overflow-y: auto;
  min-height: 400px;

  h1, h2, h3, h4, h5, h6 {
    color: ${glassTheme.colors.text.primary};
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    color: ${glassTheme.colors.text.secondary};
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ul, ol {
    color: ${glassTheme.colors.text.secondary};
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }

  code {
    background: rgba(139, 92, 246, 0.2);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
  }

  pre {
    background: rgba(0, 0, 0, 0.4);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin-bottom: 1rem;

    code {
      background: none;
      padding: 0;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;

    th, td {
      padding: 0.75rem;
      border: 1px solid ${glassTheme.colors.border};
      color: ${glassTheme.colors.text.secondary};
  }

  img {
    max-width: 100%;
    border-radius: 8px;
  }
`;

type EditorTab = 'content' | 'keywords' | 'exercise';
type ContentViewMode = 'code' | 'rendered';

type ModalType = 'add-main-category' | 'add-subcategory' | 'add-topic' | 'save-version' | null;

// State passed via `navigate('/admin/content', { state })` from the dashboards
// so cards there can deep-link straight into the relevant topic/category
// instead of always landing on the generic library view.
interface ContentManagementNavState {
  focusTopicSlug?: string | null;
  focusTopicId?: string | null;
  focusTab?: EditorTab;
  focusCategoryCode?: string | null;
  focusCategoryName?: string | null;
  draftsOnly?: boolean;
  guideMessage?: string;
  openCategoryModal?: boolean;
}

interface ContentVersion {
  version: string; // Format: 'userEmail-topicTitle-version x'
  title: string;
  htmlContent: string;
  userId: string;
  userEmail: string;
  timeStamp: string;
  isLatest: boolean;
}

// Extended topic type that combines API data with FLAT_TOPICS data
interface ExtendedTopic {
  id: string;
  code: string;
  title: string;
  chineseTitle: string;
  description: string;
  mainCategoryCode: string;
  mainCategoryName: string;
  subCategoryCode: string;
  subCategoryName: string;
  apiTopic?: Topic;  // API data if exists in database
  hasContent: boolean;
}

// Exercise Editor Component
interface ExerciseEditorProps {
  selectedTopic: ExtendedTopic | null;
  onSave: (exerciseHtml: string) => Promise<void>;
  saving: boolean;
}

const ExerciseEditor: React.FC<ExerciseEditorProps> = ({ selectedTopic, onSave, saving }) => {
  const [exerciseHtml, setExerciseHtml] = useState('');
  const [viewMode, setViewMode] = useState<'code' | 'rendered'>('code');
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);

  const versions = selectedTopic?.apiTopic?.exercises || [];
  const latest = pickLatestExercise(versions as any);

  // Load the LATEST version into the editor. exercises[] is version history, so
  // taking exercises[0] could load a stale copy and then save it back as the
  // newest version, silently reverting the exercise.
  useEffect(() => {
    setExerciseHtml(readExerciseHtml(pickLatestExercise(
      (selectedTopic?.apiTopic?.exercises || []) as any
    )) || '');
  }, [selectedTopic]);

  const handleEditorDidMount = (editor: Monaco.editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;
  };

  const handleSaveClick = async () => {
    if (!exerciseHtml || exerciseHtml.trim() === '') {
      toast.error('没有练习内容可保存');
      return;
    }
    await onSave(exerciseHtml);
  };

  if (!selectedTopic) {
    return (
      <p style={{ color: glassTheme.colors.text.muted }}>
        请先选择一个主题
      </p>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ color: glassTheme.colors.text.primary, marginBottom: '0.5rem' }}>
          练习题编辑器
        </h3>
        <p style={{ color: glassTheme.colors.text.muted, fontSize: '0.875rem' }}>
          在此编辑HTML格式的练习题内容。支持LaTeX公式（使用 $...$ 或 $$...$$）。
        </p>
      </div>

      <ViewModeToggle style={{ marginBottom: '1rem' }}>
        <Tab
          $active={viewMode === 'code'}
          onClick={() => setViewMode('code')}
        >
          📝 代码编辑
        </Tab>
        <Tab
          $active={viewMode === 'rendered'}
          onClick={() => setViewMode('rendered')}
        >
          👁️ 预览渲染
        </Tab>
        <SaveButton
          onClick={handleSaveClick}
          disabled={saving || !exerciseHtml}
          style={{ marginLeft: 'auto' }}
        >
          <span>{saving ? '保存中...' : '💾 保存练习'}</span>
        </SaveButton>
      </ViewModeToggle>

      {viewMode === 'code' && (
        <CodeEditorContainer>
          <Editor
            height="400px"
            defaultLanguage="html"
            value={exerciseHtml}
            onChange={(value) => setExerciseHtml(value || '')}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
            }}
          />
        </CodeEditorContainer>
      )}

      {viewMode === 'rendered' && (
        <IframeContainer>
          <iframe
            srcDoc={`
              <!DOCTYPE html>
              <html>
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <link rel="stylesheet" href="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.css">
                <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.js"></script>
                <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/contrib/auto-render.min.js"></script>
                <style>
                  body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    padding: 20px;
                    line-height: 1.6;
                    color: #e0e0e0;
                    background: #1a1a2e;
                  }
                  h1, h2, h3 { color: #fff; }
                  code {
                    background: #2a2a4a;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-family: 'Fira Code', monospace;
                  }
                  pre {
                    background: #2a2a4a;
                    padding: 1rem;
                    border-radius: 8px;
                    overflow-x: auto;
                  }
                  table {
                    border-collapse: collapse;
                    width: 100%;
                    margin: 1rem 0;
                  }
                  th, td {
                    padding: 0.75rem;
                    border: 1px solid #3a3a5a;
                  }
                  img {
                    max-width: 100%;
                    border-radius: 8px;
                  }
                  .question {
                    margin-bottom: 1.5rem;
                    padding: 1rem;
                    background: rgba(255,255,255,0.05);
                    border-radius: 8px;
                    border-left: 3px solid #6366f1;
                  }
                  .options {
                    margin-left: 1rem;
                  }
                  .option {
                    margin: 0.5rem 0;
                    padding: 0.5rem;
                    background: rgba(255,255,255,0.03);
                    border-radius: 4px;
                  }
                </style>
              </head>
              <body>
                ${exerciseHtml}
                <script>
                  document.addEventListener("DOMContentLoaded", function() {
                    renderMathInElement(document.body, {
                      delimiters: [
                        {left: "$$", right: "$$", display: true},
                        {left: "$", right: "$", display: false},
                        {left: "\\\\(", right: "\\\\)", display: false},
                        {left: "\\\\[", right: "\\\\]", display: true}
                      ]
                    });
                  });
                </script>
              </body>
              </html>
            `}
            style={{
              width: '100%',
              height: '400px',
              border: 'none',
              borderRadius: '8px',
              background: '#1a1a2e'
            }}
            title="练习预览"
          />
        </IframeContainer>
      )}
    </div>
  );
};

export const ContentManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state || {}) as ContentManagementNavState;
  const { user } = useAuthStore();
  const [allTopics, setAllTopics] = useState<ExtendedTopic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<ExtendedTopic | null>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>('content');
  const [htmlContent, setHtmlContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('');
  const [viewMode, setViewMode] = useState<ContentViewMode>('code');
  const [currentVersion, setCurrentVersion] = useState<ContentVersion | null>(null);
  const [useMonacoEditor, setUseMonacoEditor] = useState(false);
  const [draftsOnly, setDraftsOnly] = useState(!!navState.draftsOnly);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const deepLinkAppliedRef = useRef(false);

  // Modal state
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    chinese_name: '',
    icon: '',
    color: '#3498db'
  });

  // Save version form state
  const [saveVersionForm, setSaveVersionForm] = useState({
    version: '',
    title: ''
  });

  // Collapsed categories state - initialize with all categories collapsed by default
  const [collapsedMainCategories, setCollapsedMainCategories] = useState<Set<string>>(
    new Set(TOPICS_HIERARCHY.map(cat => cat.code))
  );
  const [collapsedSubCategories, setCollapsedSubCategories] = useState<Set<string>>(
    new Set(
      TOPICS_HIERARCHY.flatMap(cat =>
        cat.subcategories.map(sub => sub.code)
      )
    )
  );

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (html: string): string => {
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#40;/g, '(')   // Decode left parenthesis
      .replace(/&#41;/g, ')')   // Decode right parenthesis
      .replace(/&lparen;/g, '(')
      .replace(/&rparen;/g, ')');
  };

  // Cleanup Monaco editor on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }
    };
  }, []);

  // Configure Monaco worker before mount
  const handleEditorBeforeMount: BeforeMount = (monaco) => {
    // Set up the dummy worker for all worker types
    (window as any).MonacoEnvironment = {
      getWorker: function (_workerId: string, _label: string) {
        return new DummyWorker() as any;
      }
    };
  };

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Update layout when editor mounts
    setTimeout(() => {
      editor.layout();
    }, 100);
  };

  const toggleMainCategory = (code: string) => {
    setCollapsedMainCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const toggleSubCategory = (code: string) => {
    setCollapsedSubCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  // Load topics from API and merge with FLAT_TOPICS
  useEffect(() => {
    const loadTopics = async () => {
      try {
        setLoading(true);

        // Build extended topics from FLAT_TOPICS first (always available)
        let extendedTopics: ExtendedTopic[] = FLAT_TOPICS.map(ft => ({
          id: `topic-${ft.code}`,
          code: ft.code,
          title: ft.title,
          chineseTitle: ft.chineseTitle,
          description: ft.description,
          mainCategoryCode: ft.mainCategoryCode,
          mainCategoryName: ft.mainCategoryName,
          subCategoryCode: ft.subCategoryCode,
          subCategoryName: ft.subCategoryName,
          apiTopic: undefined,
          hasContent: false
        }));

        try {
          // Fetch ALL topics (published + drafts) to enrich the static data —
          // content managers/admins must be able to see and edit unpublished
          // topics (metadata only here; full content loaded on demand).
          // NOTE: the `/api/topics` route only applies its published-only
          // filter when `published` is literally the string 'true'; passing
          // `false` here deliberately returns every topic regardless of
          // publish state (see server/routes/topics.ts).
          const response = await getAllTopics({ published: false, limit: 1000 });

          // Create a map of API topics by their slug/code for easy lookup
          const apiTopicMap = new Map<string, Topic>();
          response.topics.forEach(apiTopic => {
            const key = apiTopic.slug?.toLowerCase() || apiTopic.id?.toLowerCase() || apiTopic.title?.toLowerCase();
            if (key) {
              apiTopicMap.set(key, apiTopic);
            }
          });

          // Merge API data into extended topics
          extendedTopics = extendedTopics.map(ft => {
            let apiTopic = apiTopicMap.get(ft.code.toLowerCase());
            if (!apiTopic) {
              apiTopic = Array.from(apiTopicMap.values()).find(at =>
                at.slug?.toLowerCase() === ft.code.toLowerCase() ||
                at.id?.toLowerCase() === ft.code.toLowerCase() ||
                at.title?.toLowerCase() === ft.title.toLowerCase() ||
                at.title === ft.chineseTitle
              );
            }

            return {
              ...ft,
              id: apiTopic?._id || ft.id,
              apiTopic: apiTopic,
              hasContent: !!apiTopic?._id
            };
          });

          // Also add any API topics that don't exist in FLAT_TOPICS
          const flatTopicCodes = new Set(FLAT_TOPICS.map(ft => ft.code.toLowerCase()));
          const flatTopicChineseTitles = new Set(FLAT_TOPICS.map(ft => ft.chineseTitle));

          response.topics.forEach((apiTopic: Topic) => {
            const slug = apiTopic.slug?.toLowerCase() || '';
            const title = apiTopic.title?.toLowerCase() || '';
            const existsInFlat = flatTopicCodes.has(slug) ||
                                 flatTopicCodes.has(apiTopic.id?.toLowerCase() || '') ||
                                 flatTopicChineseTitles.has(apiTopic.title || '') ||
                                 Array.from(flatTopicCodes).some(code => title.includes(code) || code.includes(title));

            if (!existsInFlat) {
              // Map the category's real code (not its raw Mongo _id) so this
              // topic can actually be found under the matching TOPICS_HIERARCHY
              // entry in the sidebar below. Category slugs are consistently
              // `code.toLowerCase()` in this dataset (verified against the
              // Category collection), so slug.toUpperCase() recovers the code
              // without requiring a server-side populate change.
              const categoryObj = typeof apiTopic.category === 'object' ? apiTopic.category : undefined;
              const resolvedMainCode = categoryObj?.slug?.toUpperCase() || (typeof apiTopic.category === 'string' ? apiTopic.category : '') || '';
              extendedTopics.push({
                id: apiTopic._id,
                code: apiTopic.slug || apiTopic.id,
                title: apiTopic.title || '',
                chineseTitle: apiTopic.title || '',
                description: apiTopic.description || '',
                mainCategoryCode: resolvedMainCode,
                mainCategoryName: categoryObj?.name || '',
                subCategoryCode: (apiTopic as any).subcategoryCode || '',
                subCategoryName: (apiTopic as any).subcategoryName || '',
                apiTopic: apiTopic,
                hasContent: !!apiTopic?._id
              });
            }
          });

          console.log(`Loaded ${extendedTopics.length} topics (${response.topics.length} with content in database)`);
        } catch (apiError) {
          console.warn('API topics fetch failed, using static data only:', apiError);
        }

        setAllTopics(extendedTopics);
      } catch (error) {
        console.error('Failed to load topics:', error);
        toast.error('加载主题失败');
      } finally {
        setLoading(false);
      }
    };

    loadTopics();
  }, []);

  const [contentLoading, setContentLoading] = useState(false);

  const applyTopicContent = (topic: ExtendedTopic) => {
    const contentArray = topic.apiTopic?.learningContent;

    if (contentArray && Array.isArray(contentArray) && contentArray.length > 0) {
      const latestVersion = contentArray.find((v: any) => v.isLatest) || contentArray[0];
      if (latestVersion) {
        setHtmlContent(latestVersion.htmlContent || '');
        setCurrentVersion(latestVersion);
      } else {
        setHtmlContent('');
        setCurrentVersion(null);
      }
    } else if (topic.apiTopic?.content) {
      const content = topic.apiTopic.content;
      const fallbackHtml = `
        <h2>定义</h2>
        <p>${content.definition || ''}</p>
        <h2>核心要点</h2>
        <ul>${(content.keyPoints || []).map((p: string) => `<li>${p}</li>`).join('')}</ul>
        <h2>总结</h2>
        <p>${content.summary || ''}</p>
      `;
      setHtmlContent(fallbackHtml);
      setCurrentVersion(null);
    } else {
      setHtmlContent('');
      setCurrentVersion(null);
    }
  };

  const handleTopicSelect = async (topic: ExtendedTopic, initialTab: EditorTab = 'content') => {
    setSelectedTopic(topic);
    setActiveTab(initialTab);
    setViewMode('code');

    // If topic has full content already (cached or freshly loaded), use it
    const hasFullContent = !!(topic.apiTopic?.learningContent || topic.apiTopic?.content);
    if (hasFullContent) {
      applyTopicContent(topic);
      return;
    }

    // If topic has an API ID, fetch full content on demand
    if (topic.apiTopic?._id) {
      try {
        setContentLoading(true);
        const fullTopic = await getTopicById(topic.apiTopic._id);
        // Cache the full topic data
        const updatedTopic = {
          ...topic,
          apiTopic: fullTopic,
          hasContent: !!(fullTopic?.learningContent || fullTopic?.content)
        };
        // Update in allTopics array for caching
        setAllTopics(prev => prev.map(t =>
          (t.apiTopic?._id || t.id) === (updatedTopic.apiTopic?._id || updatedTopic.id) ? updatedTopic : t
        ));
        setSelectedTopic(updatedTopic);
        applyTopicContent(updatedTopic);
      } catch (err) {
        console.error('Failed to load topic content:', err);
        setHtmlContent('');
        setCurrentVersion(null);
      } finally {
        setContentLoading(false);
      }
    } else {
      setHtmlContent('');
      setCurrentVersion(null);
    }
  };

  const handleSave = () => {
    if (!selectedTopic) return;
    if (!htmlContent || htmlContent.trim() === '') {
      toast.error('没有内容可保存');
      return;
    }

    // Set default values for the save version form
    const contentArray = selectedTopic.apiTopic?.learningContent;
    const nextVersionNumber = contentArray?.length || 0;
    const defaultVersion = `Version ${nextVersionNumber + 1}`;
    const defaultTitle = `${selectedTopic.chineseTitle} - ${new Date().toLocaleDateString('zh-CN')}`;

    setSaveVersionForm({
      version: defaultVersion,
      title: defaultTitle
    });

    // Show the modal instead of directly saving
    setModalType('save-version');
  };

  const handleSaveChanges = async () => {
    if (!selectedTopic) return;
    if (!htmlContent || htmlContent.trim() === '') {
      toast.error('没有内容可保存');
      return;
    }

    // Check if there's an existing version to update
    const contentArray = selectedTopic.apiTopic?.learningContent;
    if (!selectedTopic.apiTopic?._id || !contentArray || contentArray.length === 0) {
      toast.error('没有现有版本可以修改，请使用"保存新版本"创建第一个版本');
      return;
    }

    setSaving(true);

    try {
      // Get current user info
      const user = useAuthStore.getState().user;

      // Find the latest version and update its htmlContent
      const updatedLearningContent = contentArray.map((v: any) => {
        if (v.isLatest) {
          return {
            ...v,
            htmlContent: htmlContent
          };
        }
        return v;
      });

      // Update with the modified learningContent array
      await updateTopic(selectedTopic.apiTopic._id, {
        learningContent: updatedLearningContent,
        userEmail: user?.email || ''
      });

      toast.success('修改已保存到当前版本');

      // Reload topics to get the updated data
      const response = await getAllTopics({ published: true, limit: 1000 });
      const apiTopicMap = buildTopicMap(response.topics);

      const updatedTopics = allTopics.map((t) => {
        if (t.id === selectedTopic.id) {
          const codeKey = t.code?.toLowerCase()?.trim();

          const apiTopic = (codeKey && apiTopicMap.get(codeKey)) ||
                          Array.from(apiTopicMap.values()).find(at =>
                            at.slug?.toLowerCase()?.trim() === codeKey ||
                            at.id?.toLowerCase()?.trim() === codeKey
                          );
          return {
            ...t,
            apiTopic: apiTopic || undefined,
            hasContent: !!(apiTopic?.learningContent || apiTopic?.content)
          };
        }
        return t;
      });

      setAllTopics(updatedTopics);

      // Update the selected topic with the new content
      const updatedTopic = updatedTopics.find(t => t.id === selectedTopic.id);
      if (updatedTopic) {
        setSelectedTopic(updatedTopic);
      }
    } catch (error: any) {
      console.error('Save changes error:', error);
      toast.error(error.response?.data?.message || error.message || '保存修改失败');
    } finally {
      setSaving(false);
    }
  };

  const performSave = async () => {
    if (!selectedTopic) return;

    setSaving(true);
    setModalType(null); // Close the modal

    try {
      // Get current user info
      const user = useAuthStore.getState().user;

      if (selectedTopic.apiTopic?._id) {
        // Topic exists in database - update it
        // Backend expects htmlContent as STRING when saveAsNewVersion: true
        // Backend internally handles version array creation
        await updateTopic(selectedTopic.apiTopic._id, {
          htmlContent: htmlContent,  // Send as STRING, backend creates version array
          saveAsNewVersion: true,
          versionTitle: saveVersionForm.title,
          userEmail: user?.email || ''
        });
        toast.success('内容版本已保存');

        // Reload topics to get the updated data
        const response = await getAllTopics({ published: true, limit: 1000, full: true });
        const apiTopicMap = buildTopicMap(response.topics);

        const updatedTopics = allTopics.map((t) => {
          if (t.id === selectedTopic.id) {
            const codeKey = t.code?.toLowerCase()?.trim();

            // Try direct map lookup first (O(1)), fallback to linear search (O(n))
            const apiTopic = (codeKey && apiTopicMap.get(codeKey)) ||
                            Array.from(apiTopicMap.values()).find(at =>
                              at.slug?.toLowerCase()?.trim() === codeKey ||
                              at.id?.toLowerCase()?.trim() === codeKey
                            );
            return {
              ...t,
              apiTopic: apiTopic || undefined,
              hasContent: !!apiTopic?._id
            };
          }
          return t;
        });

        setAllTopics(updatedTopics);
        setSelectedTopic(updatedTopics.find(t => t.id === selectedTopic.id) || selectedTopic);
      } else {
        // Topic doesn't exist in our local state - try to create it
        toast.loading('正在保存内容...', { id: 'create-topic' });

        // For new topics, backend expects learningContent as an array of version objects
        const contentVersion = {
          version: `${user?.email || 'user'}-${selectedTopic.chineseTitle}-${saveVersionForm.version}`,
          title: saveVersionForm.title,
          htmlContent: htmlContent,
          userId: user?.id || '',
          userEmail: user?.email || '',
          timeStamp: new Date().toISOString(),
          isLatest: true
        };

        try {
          const newTopic = await createTopic({
            title: selectedTopic.chineseTitle,
            slug: selectedTopic.code.toLowerCase(),
            description: selectedTopic.description || `${selectedTopic.chineseTitle} - ${selectedTopic.title}`,
            category: selectedTopic.mainCategoryCode,
            learningContent: [contentVersion],
            content: {},
            published: false,
            order: 0
          });

          toast.dismiss('create-topic');
          toast.success('主题已创建并保存内容');

          // Reload topics to get the updated data
          const response = await getAllTopics({ published: true, limit: 1000, full: true });
          const apiTopicMap = buildTopicMap(response.topics);

          const updatedTopics = allTopics.map((t) => {
            if (t.id === selectedTopic.id) {
              const codeKey = t.code?.toLowerCase()?.trim();

              // Try direct map lookup first (O(1)), fallback to linear search (O(n))
              const apiTopic = (codeKey && apiTopicMap.get(codeKey)) ||
                              Array.from(apiTopicMap.values()).find(at =>
                                at.slug?.toLowerCase()?.trim() === codeKey ||
                                at.id?.toLowerCase()?.trim() === codeKey
                              );
              return {
                ...t,
                apiTopic: apiTopic || undefined,
                hasContent: !!apiTopic?._id
              };
            }
            return t;
          });

          setAllTopics(updatedTopics);
          setSelectedTopic(updatedTopics.find(t => t.id === selectedTopic.id) || selectedTopic);
        } catch (createError: any) {
          // If duplicate key error, topic might already exist in database
          // Try to find it and update instead
          if (createError.response?.status === 409 || createError.response?.data?.message?.includes('already exists')) {
            toast.dismiss('create-topic');
            toast.loading('查找现有主题...', { id: 'update-existing' });

            try {
              // Try to get the existing topic by slug
              const existingTopic = await getTopicBySlug(selectedTopic.code.toLowerCase());

              if (existingTopic._id) {
                // Update the existing topic with new version
                await updateTopic(existingTopic._id, {
                  htmlContent: htmlContent,
                  saveAsNewVersion: true,
                  versionTitle: saveVersionForm.title,
                  userEmail: user?.email || ''
                });

                toast.dismiss('update-existing');
                toast.success('内容已保存到现有主题');

                // Reload topics
                const response = await getAllTopics({ published: true, limit: 1000, full: true });
                const apiTopicMap = buildTopicMap(response.topics);

                const updatedTopics = allTopics.map((t) => {
                  if (t.id === selectedTopic.id) {
                    const codeKey = t.code?.toLowerCase()?.trim();

                    const apiTopic = (codeKey && apiTopicMap.get(codeKey)) ||
                                    Array.from(apiTopicMap.values()).find(at =>
                                      at.slug?.toLowerCase()?.trim() === codeKey ||
                                      at.id?.toLowerCase()?.trim() === codeKey ||
                                      at._id === existingTopic._id
                                    );
                    return {
                      ...t,
                      id: apiTopic?._id || t.id,
                      apiTopic: apiTopic || undefined,
                      hasContent: !!apiTopic?._id
                    };
                  }
                  return t;
                });

                setAllTopics(updatedTopics);
                const updatedTopic = updatedTopics.find(t => t.id === selectedTopic.id || t.apiTopic?._id === existingTopic._id);
                setSelectedTopic(updatedTopic || selectedTopic);
              } else {
                toast.dismiss('update-existing');
                toast.error('无法找到现有主题');
              }
            } catch (updateError: any) {
              toast.dismiss('update-existing');
              console.error('Update existing topic error:', updateError);
              toast.error(updateError.response?.data?.message || updateError.message || '保存失败');
            }
          } else {
            throw createError; // Re-throw if not a duplicate key error
          }
        }
      }
    } catch (error: any) {
      console.error('Save error:', error);
      toast.dismiss('create-topic');
      toast.error(error.response?.data?.message || error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateContent = async () => {
    if (!selectedTopic) {
      toast.error('请先选择一个主题');
      return;
    }

    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('topicTitle', selectedTopic.chineseTitle);
      formData.append('topicDescription', selectedTopic.description);
      if (prompt) {
        formData.append('prompt', prompt);
      }
      if (uploadedFile) {
        formData.append('contentFile', uploadedFile);
      }

      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/ai/generate-content`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setHtmlContent(response.data.data.htmlContent);
        toast.success('内容生成成功！');
        // Clear file and prompt after successful generation
        setUploadedFile(null);
        setPrompt('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error: any) {
      console.error('Generate content error:', error);
      toast.error(error.response?.data?.message || '生成内容失败');
    } finally {
      setGenerating(false);
    }
  };

  // Save exercise content as a NEW VERSION.
  //
  // This used to send `exercises: [exerciseData]`, which replaced the whole
  // array and destroyed every earlier copy. The server now appends a version
  // and clears isLatest on the rest, matching how learning content works.
  const handleSaveExercise = async (exerciseHtml: string) => {
    if (!selectedTopic || !selectedTopic.apiTopic?._id) {
      toast.error('请先保存主题内容以创建主题记录');
      return;
    }

    setSaving(true);
    try {
      await saveExerciseVersion(selectedTopic.apiTopic._id, {
        exerciseHtml,
        title: selectedTopic.apiTopic.title || '练习题',
        userEmail: user?.email
      });

      toast.success('练习内容已保存为新版本');

      // Reload topics to get the updated data
      const response = await getAllTopics({ published: true, limit: 1000 });
      const apiTopicMap = buildTopicMap(response.topics);

      const updatedTopics = allTopics.map((t) => {
        if (t.id === selectedTopic.id) {
          const codeKey = t.code?.toLowerCase()?.trim();
          const apiTopic = (codeKey && apiTopicMap.get(codeKey)) ||
                          Array.from(apiTopicMap.values()).find(at =>
                            at.slug?.toLowerCase()?.trim() === codeKey ||
                            at.id?.toLowerCase()?.trim() === codeKey
                          );
          return {
            ...t,
            apiTopic
          };
        }
        return t;
      });

      setAllTopics(updatedTopics);
    } catch (error: any) {
      console.error('Save exercise error:', error);
      toast.error(error.response?.data?.message || '保存练习失败');
    } finally {
      setSaving(false);
    }
  };

  // Modal handlers
  const openAddMainCategoryModal = () => {
    setModalType('add-main-category');
    setFormData({ code: '', name: '', chinese_name: '', icon: '', color: '#3498db' });
  };

  const openAddSubcategoryModal = (mainCategoryCode: string) => {
    setModalType('add-subcategory');
    setSelectedParentCategory(mainCategoryCode);
    setFormData({ code: '', name: '', chinese_name: '', icon: '', color: '#3498db' });
  };

  const openAddTopicModal = (mainCategoryCode: string, subCategoryCode: string) => {
    setModalType('add-topic');
    setSelectedParentCategory(`${mainCategoryCode}|${subCategoryCode}`);
    setFormData({ code: '', name: '', chinese_name: '', icon: '', color: '#3498db' });
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedParentCategory(null);
    setFormData({ code: '', name: '', chinese_name: '', icon: '', color: '#3498db' });
  };

  const handleModalSubmit = async () => {
    try {
      // Validate form data
      if (!formData.code || !formData.name || !formData.chinese_name) {
        toast.error('请填写必填字段（代码、名称、中文名称）');
        return;
      }

      // Generate slug from code if not provided
      const slug = formData.code.toLowerCase();

      if (modalType === 'add-main-category') {
        // Create main category
        await createMainCategory({
          code: formData.code,
          name: formData.name,
          chinese_name: formData.chinese_name,
          slug: slug,
          description: `${formData.chinese_name} - ${formData.name}`,
          icon: formData.icon || '',
          color: formData.color,
          topics_count: 0,
          order: 0
        });
        toast.success('主分类创建成功！');
      } else if (modalType === 'add-subcategory') {
        // Create subcategory under parent category
        if (!selectedParentCategory) {
          toast.error('缺少父分类信息');
          return;
        }
        await createSubcategory({
          parentCategoryCode: selectedParentCategory,
          code: formData.code,
          name: formData.name,
          chinese_name: formData.chinese_name,
          slug: slug,
          description: `${formData.chinese_name} - ${formData.name}`,
          topics_count: 0,
          order: 0
        });
        toast.success('子分类创建成功！');
      } else if (modalType === 'add-topic') {
        // Parse selectedParentCategory which contains mainCategory|subCategory
        if (!selectedParentCategory || !selectedParentCategory.includes('|')) {
          toast.error('缺少分类信息');
          return;
        }

        const [mainCategoryCode, subCategoryCode] = selectedParentCategory.split('|');

        // Create topic (this creates an empty topic entry in the database)
        // Note: The actual category reference needs to be resolved from the database
        // For now, we'll create a minimal topic entry
        await createTopic({
          title: formData.name,
          slug: slug,
          description: formData.chinese_name || `${formData.chinese_name} - ${formData.name}`,
          category: mainCategoryCode, // This will need to be the actual category ID
          htmlContent: '',
          content: {},
          published: false,
          order: 0
        });
        toast.success('主题创建成功！');
      }

      closeModal();

      // Reload topics to refresh the list
      // Note: This won't update TOPICS_HIERARCHY as it's static data
      // In a full implementation, you would also fetch categories and update the hierarchy
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error: any) {
      console.error('Error creating item:', error);
      const errorMessage = error.response?.data?.message || error.message || '创建失败，请稍后重试';
      toast.error(errorMessage);
    }
  };

  // Apply a deep link passed via `navigate('/admin/content', { state })` (e.g. from
  // the Content Manager dashboard cards) exactly once, after topics have loaded.
  // Supports jumping straight to a topic (optionally on a specific editor tab),
  // expanding a specific category, opening the "add category" modal, or scoping
  // the sidebar to drafts only.
  useEffect(() => {
    if (loading || deepLinkAppliedRef.current || allTopics.length === 0) return;
    if (!location.state) return;
    deepLinkAppliedRef.current = true;

    if (navState.guideMessage) {
      toast(navState.guideMessage, { icon: '💡' });
    }

    if (navState.openCategoryModal) {
      openAddMainCategoryModal();
    }

    const targetSlug = navState.focusTopicSlug?.toLowerCase();
    const targetId = navState.focusTopicId;
    if (targetSlug || targetId) {
      const matchedTopic = allTopics.find(t =>
        (targetId && (t.apiTopic?._id === targetId || t.id === targetId)) ||
        (targetSlug && (
          t.code.toLowerCase() === targetSlug ||
          t.apiTopic?.slug?.toLowerCase() === targetSlug
        ))
      );
      if (matchedTopic) {
        const knownSubCodes = new Set(
          TOPICS_HIERARCHY.find(cat => cat.code === matchedTopic.mainCategoryCode)
            ?.subcategories.map(sub => sub.code) || []
        );
        const subKeyToExpand = knownSubCodes.has(matchedTopic.subCategoryCode)
          ? matchedTopic.subCategoryCode
          : `${matchedTopic.mainCategoryCode}__other`;
        setCollapsedMainCategories(prev => {
          const next = new Set(prev);
          next.delete(matchedTopic.mainCategoryCode);
          return next;
        });
        setCollapsedSubCategories(prev => {
          const next = new Set(prev);
          next.delete(subKeyToExpand);
          return next;
        });
        handleTopicSelect(matchedTopic, navState.focusTab || 'content');
      } else {
        toast.error('未找到该知识元，可能已被删除或尚未同步');
      }
      return;
    }

    const targetCategoryCode = navState.focusCategoryCode?.toLowerCase();
    const targetCategoryName = navState.focusCategoryName?.toLowerCase();
    if (targetCategoryCode || targetCategoryName) {
      const matchedCategory = TOPICS_HIERARCHY.find(cat =>
        (targetCategoryCode && cat.code.toLowerCase() === targetCategoryCode) ||
        (targetCategoryName && cat.chinese_name.toLowerCase() === targetCategoryName)
      );
      if (matchedCategory) {
        setCollapsedMainCategories(prev => {
          const next = new Set(prev);
          next.delete(matchedCategory.code);
          return next;
        });
        requestAnimationFrame(() => {
          document.getElementById(`content-category-${matchedCategory.code}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        toast('该分类的知识元编辑视图暂未关联，可在左侧目录中手动查找', { icon: 'ℹ️' });
      }
    }
  }, [loading, allTopics, location.state]);

  return (
    <DashboardLayout
      title="内容管理系统"
      subtitle="使用富文本编辑器管理学习主题内容"
    >
      <ContentGrid>
          <Sidebar>
            <SidebarContainer>
              <SidebarTitle>
                学习主题
                <IconButton onClick={openAddMainCategoryModal} title="新增主分类">+</IconButton>
              </SidebarTitle>
              {draftsOnly && (
                <DraftFilterBanner>
                  <span>仅显示待发布知识元</span>
                  <button onClick={() => setDraftsOnly(false)}>清除筛选</button>
                </DraftFilterBanner>
              )}
              <TopicList>
              {TOPICS_HIERARCHY.map((mainCategory) => {
                const isDraftTopic = (t: ExtendedTopic) => !!t.apiTopic && t.apiTopic.published === false;
                const mainCategoryTopics = allTopics.filter(t =>
                  t.mainCategoryCode === mainCategory.code && (!draftsOnly || isDraftTopic(t))
                );
                if (mainCategoryTopics.length === 0) return null;

                const isMainCollapsed = collapsedMainCategories.has(mainCategory.code);

                return (
                  <div key={mainCategory.code} id={`content-category-${mainCategory.code}`}>
                    <TopicCategoryLabel
                      $color={mainCategory.color}
                      onClick={() => toggleMainCategory(mainCategory.code)}
                    >
                      <CollapseIcon $collapsed={isMainCollapsed}>▼</CollapseIcon>
                      <span>{mainCategory.chinese_name}</span>
                      <span style={{ fontSize: '0.8rem', opacity: 0.7, fontWeight: 400 }}>({mainCategoryTopics.length}个主题)</span>
                      <IconButton onClick={(e) => { e.stopPropagation(); openAddSubcategoryModal(mainCategory.code); }} title="新增子分类">+</IconButton>
                    </TopicCategoryLabel>

                    {!isMainCollapsed && mainCategory.subcategories.map((subcategory) => {
                      const subCategoryTopics = allTopics.filter(t =>
                        t.subCategoryCode === subcategory.code && (!draftsOnly || isDraftTopic(t))
                      );
                      // Show subcategory even if empty (can add topics)
                      const isSubCollapsed = collapsedSubCategories.has(subcategory.code);

                      return (
                        <div key={subcategory.code}>
                          <TopicCategoryLabel
                            $color={mainCategory.color}
                            $isSubCategory={true}
                            onClick={() => toggleSubCategory(subcategory.code)}
                          >
                            <CollapseIcon $collapsed={isSubCollapsed}>▼</CollapseIcon>
                            {subcategory.chinese_name}
                            <IconButton onClick={(e) => { e.stopPropagation(); openAddTopicModal(mainCategory.code, subcategory.code); }} title="新增主题">+</IconButton>
                          </TopicCategoryLabel>
                          {!isSubCollapsed && (
                            <>
                              {subCategoryTopics.map((topic) => (
                                <TopicListItem
                                  key={topic.id}
                                  $active={selectedTopic?.id === topic.id}
                                  onClick={() => handleTopicSelect(topic)}
                                >
                                  <TopicListItemText>
                                    <HeartCheckbox>
                                      {topic.apiTopic?._id ? '❤️' : '🤍'}
                                    </HeartCheckbox>
                                    {topic.chineseTitle}
                                    {!topic.hasContent && <span style={{ opacity: 0.5, fontSize: '0.85em', marginLeft: '0.5rem' }}>(无内容)</span>}
                                  </TopicListItemText>
                                </TopicListItem>
                              ))}
                              {subCategoryTopics.length === 0 && (
                                <div style={{ padding: '0.5rem 1.25rem 0.5rem 2.5rem', opacity: 0.5, fontSize: '0.85rem' }}>
                                  暂无主题
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}

                    {/* Topics saved directly in the database with no matching static
                        subcategory (e.g. drafts authored outside the Excel-derived
                        hierarchy) would otherwise be permanently invisible here.
                        Surface them in a catch-all bucket instead of silently
                        dropping them. */}
                    {!isMainCollapsed && (() => {
                      const knownSubCodes = new Set(mainCategory.subcategories.map(sub => sub.code));
                      const orphanTopics = mainCategoryTopics.filter(t => !knownSubCodes.has(t.subCategoryCode));
                      if (orphanTopics.length === 0) return null;
                      const orphanKey = `${mainCategory.code}__other`;
                      const isOrphanCollapsed = collapsedSubCategories.has(orphanKey);
                      return (
                        <div key={orphanKey}>
                          <TopicCategoryLabel
                            $color={mainCategory.color}
                            $isSubCategory={true}
                            onClick={() => toggleSubCategory(orphanKey)}
                          >
                            <CollapseIcon $collapsed={isOrphanCollapsed}>▼</CollapseIcon>
                            其他主题
                            <span style={{ fontSize: '0.75rem', opacity: 0.7, fontWeight: 400, marginLeft: '0.35rem' }}>
                              ({orphanTopics.length})
                            </span>
                          </TopicCategoryLabel>
                          {!isOrphanCollapsed && orphanTopics.map((topic) => (
                            <TopicListItem
                              key={topic.id}
                              $active={selectedTopic?.id === topic.id}
                              onClick={() => handleTopicSelect(topic)}
                            >
                              <TopicListItemText>
                                <HeartCheckbox>
                                  {topic.apiTopic?._id ? '❤️' : '🤍'}
                                </HeartCheckbox>
                                {topic.chineseTitle}
                                {!topic.hasContent && <span style={{ opacity: 0.5, fontSize: '0.85em', marginLeft: '0.5rem' }}>(无内容)</span>}
                              </TopicListItemText>
                            </TopicListItem>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </TopicList>
            </SidebarContainer>
          </Sidebar>

          <EditorPanel>
            {selectedTopic ? (
              <>
                <EditorHeader>
                  <EditorTitle>{selectedTopic.chineseTitle}</EditorTitle>
                </EditorHeader>

                {contentLoading && (
                  <div style={{ padding: '2rem', textAlign: 'center', color: glassTheme.colors.text.muted }}>
                    加载中...
                  </div>
                )}

                <Tabs>
                  <Tab $active={activeTab === 'content'} onClick={() => setActiveTab('content')}>
                    📄 内容
                  </Tab>
                  <Tab $active={activeTab === 'keywords'} onClick={() => setActiveTab('keywords')}>
                    🔑 关键词
                  </Tab>
                  <Tab $active={activeTab === 'exercise'} onClick={() => setActiveTab('exercise')}>
                    📝 练习
                  </Tab>
                </Tabs>

                {!contentLoading && (
                <EditorContent>
                  {activeTab === 'content' && (
                    <>
                      {/* Content Generation Panel */}
                      <ContentGenerationPanel>
                        <FileUploadSection>
                          <FileInputLabel>上传参考文件 (PDF, Word, 图片):</FileInputLabel>
                          <FileInput
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setUploadedFile(file);
                              }
                            }}
                          />
                          {uploadedFile && (
                            <div style={{ fontSize: '0.85rem', color: glassTheme.colors.text.secondary }}>
                              已选择: {uploadedFile.name}
                            </div>
                          )}
                        </FileUploadSection>

                        <FileUploadSection>
                          <FileInputLabel>生成提示词 (可选):</FileInputLabel>
                          <PromptTextarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="输入自定义提示词来指导AI生成内容...&#10;&#10;例如：&#10;- 请详细解释该概念的定义和核心要点&#10;- 包含具体的统计应用案例&#10;- 添加数学公式推导过程"
                          />
                        </FileUploadSection>

                        <GenerateButton
                          onClick={handleGenerateContent}
                          disabled={generating}
                        >
                          <span>{generating ? '生成中...' : '🤖 AI 生成内容'}</span>
                        </GenerateButton>
                      </ContentGenerationPanel>

                      {/* View Mode Toggle */}
                      <ViewModeToggle>
                        <Tab
                          $active={viewMode === 'code'}
                          onClick={() => setViewMode('code')}
                        >
                          📝 代码编辑
                        </Tab>
                        <Tab
                          $active={viewMode === 'rendered'}
                          onClick={() => setViewMode('rendered')}
                        >
                          👁️ 预览渲染
                        </Tab>
                        <SaveButton onClick={handleSaveChanges} disabled={saving || !htmlContent} style={{ marginLeft: 'auto' }}>
                          <span>{saving ? '保存中...' : '💾 保存修改'}</span>
                        </SaveButton>
                        <SaveButton onClick={handleSave} disabled={saving || !htmlContent} style={{ marginLeft: '0.5rem' }}>
                          <span>{saving ? '保存中...' : '💾 保存新版本'}</span>
                        </SaveButton>
                      </ViewModeToggle>

                      {/* Content View */}
                      {viewMode === 'code' && (
                        <>
                          {/* Toggle between Monaco Editor and Textarea */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ color: glassTheme.colors.text.muted, fontSize: '0.875rem' }}>
                              {htmlContent ? `${htmlContent.length} characters` : 'No content'}
                            </span>
                            <button
                              onClick={() => setUseMonacoEditor(!useMonacoEditor)}
                              style={{
                                padding: '0.4rem 0.8rem',
                                background: 'rgba(139, 92, 246, 0.2)',
                                border: '1px solid rgba(139, 92, 246, 0.5)',
                                borderRadius: '6px',
                                color: '#8b5cf6',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              {useMonacoEditor ? 'Switch to Textarea' : 'Switch to Monaco Editor'}
                            </button>
                          </div>

                          <CodeEditorWrapper>
                            {useMonacoEditor ? (
                              <Editor
                                key={selectedTopic?.id || 'editor'}
                                height="500px"
                                defaultLanguage="html"
                                value={htmlContent || ''}
                                onChange={(value) => {
                                  setHtmlContent(value || '');
                                }}
                                beforeMount={handleEditorBeforeMount}
                                onMount={handleEditorMount}
                                theme="vs-dark"
                                loading={<div style={{ color: '#fff', padding: '20px' }}>Loading editor...</div>}
                                options={{
                                  fontSize: 14,
                                  lineNumbers: 'on',
                                  minimap: { enabled: false },
                                  scrollBeyondLastLine: false,
                                  automaticLayout: true,
                                  quickSuggestions: false,  // Disable suggestions that require workers
                                  suggest: {
                                    showKeywords: false,
                                    showSnippets: false,
                                  },
                                  parameterHints: {
                                    enabled: false,
                                  },
                                }}
                              />
                            ) : (
                              <textarea
                                value={htmlContent}
                                onChange={(e) => setHtmlContent(e.target.value)}
                                placeholder="HTML content will appear here..."
                                style={{
                                  width: '100%',
                                  height: '500px',
                                  padding: '1rem',
                                  background: 'rgba(0, 0, 0, 0.4)',
                                  border: '1px solid ' + glassTheme.colors.border,
                                  borderRadius: '8px',
                                  color: glassTheme.colors.text.primary,
                                  fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
                                  fontSize: '0.875rem',
                                  lineHeight: '1.5',
                                  resize: 'none',
                                  outline: 'none',
                                }}
                              />
                            )}
                          </CodeEditorWrapper>
                        </>
                      )}

                      {viewMode === 'rendered' && (
                        <IframeContainer>
                          {htmlContent ? (
                            <IframeWrapper
                              srcDoc={`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                  <meta charset="UTF-8">
                                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                  <link rel="stylesheet" href="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.css">
                                  <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.js"></script>
                                  <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/contrib/auto-render.min.js"></script>
                                  <style>
                                    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; line-height: 1.6; }
                                    h1, h2, h3, h4, h5, h6 { margin-top: 1.5em; margin-bottom: 0.5em; }
                                    p { margin-bottom: 1em; }
                                    code { background: rgba(139, 92, 246, 0.2); padding: 0.2em 0.4em; border-radius: 4px; font-family: 'Courier New', monospace; }
                                    pre { background: rgba(0, 0, 0, 0.3); padding: 1rem; border-radius: 8px; overflow-x: auto; }
                                    pre code { background: none; padding: 0; }
                                  </style>
                                </head>
                                <body>
                                  ${decodeHtmlEntities(htmlContent)}
                                  <script>
                                    document.addEventListener('DOMContentLoaded', function() {
                                      renderMathInElement(document.body, {
                                        delimiters: [
                                          {left: '$$', right: '$$', display: true},
                                          {left: '$', right: '$', display: false},
                                          {left: '\\[', right: '\\]', display: true}
                                        ],
                                        throwOnError: false
                                      });
                                    });
                                  </script>
                                </body>
                                </html>
                              `}
                              title="Content Preview"
                              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                            />
                          ) : (
                            <div style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: glassTheme.colors.text.muted
                            }}>
                              暂无内容，请先生成或编辑内容
                            </div>
                          )}
                        </IframeContainer>
                      )}
                    </>
                  )}

                  {activeTab === 'keywords' && (
                    <div style={{ padding: '1.5rem' }}>
                      <p style={{ color: glassTheme.colors.text.muted }}>
                        关键词管理功能即将推出...
                      </p>
                    </div>
                  )}

                  {activeTab === 'exercise' && (
                    <div style={{ padding: '1.5rem' }}>
                      <ExerciseEditor
                        selectedTopic={selectedTopic}
                        onSave={handleSaveExercise}
                        saving={saving}
                      />
                    </div>
                  )}
                </EditorContent>
                )}
              </>
            ) : (
              <EmptyState>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <p>选择一个主题开始编辑</p>
              </EmptyState>
            )}
          </EditorPanel>
        </ContentGrid>

        {/* Modals */}
        {modalType && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>
                {modalType === 'add-main-category' && '新增主分类'}
                {modalType === 'add-subcategory' && '新增子分类'}
                {modalType === 'add-topic' && '新增主题'}
                {modalType === 'save-version' && '保存内容版本'}
              </ModalTitle>

              <FormField>
                <FormLabel>代码</FormLabel>
                <FormInput
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="例如: DS, P, RVD"
                />
              </FormField>

              <FormField>
                <FormLabel>英文名称</FormLabel>
                <FormInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如: Descriptive Statistics"
                />
              </FormField>

              <FormField>
                <FormLabel>中文名称</FormLabel>
                <FormInput
                  type="text"
                  value={formData.chinese_name}
                  onChange={(e) => setFormData({ ...formData, chinese_name: e.target.value })}
                  placeholder="例如: 描述统计"
                />
              </FormField>

              {modalType === 'add-main-category' && (
                <>
                  <FormField>
                    <FormLabel>图标</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="例如: 📊"
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>颜色</FormLabel>
                    <FormInput
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </FormField>
                </>
              )}

              {modalType === 'save-version' && (
                <>
                  <FormField>
                    <FormLabel>版本号</FormLabel>
                    <FormInput
                      type="text"
                      value={saveVersionForm.version}
                      onChange={(e) => setSaveVersionForm({ ...saveVersionForm, version: e.target.value })}
                      placeholder="例如: Version 1.0"
                    />
                  </FormField>

                  <FormField>
                    <FormLabel>版本标题</FormLabel>
                    <FormInput
                      type="text"
                      value={saveVersionForm.title}
                      onChange={(e) => setSaveVersionForm({ ...saveVersionForm, title: e.target.value })}
                      placeholder="例如: 描述统计 - 2024年1月版本"
                    />
                  </FormField>
                </>
              )}

              <ModalActions>
                <Button onClick={closeModal}>取消</Button>
                <Button $variant="primary" onClick={modalType === 'save-version' ? performSave : handleModalSubmit}>
                  确定
                </Button>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
    </DashboardLayout>
  );
};

export default ContentManagement;
